import { computed, ref } from 'vue'
import type { Canvas } from 'fabric'

type CanvasSnapshot = Record<string, unknown>

export const useCanvasHistory = () => {
  const undoStack = ref<CanvasSnapshot[]>([])
  const redoStack = ref<CanvasSnapshot[]>([])
  const isRestoring = ref(false)

  const snapshotsMatch = (first: CanvasSnapshot, second: CanvasSnapshot) =>
    JSON.stringify(first) === JSON.stringify(second)

  const getPreviousDistinctSnapshotIndex = (snapshots: CanvasSnapshot[]) => {
    if (snapshots.length < 2) return -1

    const current = snapshots[snapshots.length - 1]
    let index = snapshots.length - 2
    while (index >= 0 && snapshotsMatch(snapshots[index], current)) index -= 1
    return index
  }

  const canUndo = computed(() => getPreviousDistinctSnapshotIndex(undoStack.value) >= 0)
  const canRedo = computed(() => {
    const current = undoStack.value[undoStack.value.length - 1]
    return redoStack.value.some((snapshot) => !current || !snapshotsMatch(snapshot, current))
  })

  const readSnapshot = (canvas: Canvas): CanvasSnapshot =>
    (canvas.toJSON as (propertiesToInclude?: string[]) => CanvasSnapshot)([
      'selectable',
      'evented',
      'name',
      'isCensorMask',
      'censorId',
      'censorMode',
      'censorLevel',
      'isDrawingPath',
      'excludeFromExport',
    ])

  const save = (canvas: Canvas) => {
    if (isRestoring.value) return false

    const snapshot = readSnapshot(canvas)
    const current = undoStack.value[undoStack.value.length - 1]
    // Fabric can emit more than one event for one gesture (for example,
    // object:added plus a manual save). Only a distinct canvas state is a
    // history entry, and no-op events must not invalidate redo.
    if (current && snapshotsMatch(current, snapshot)) return false

    undoStack.value.push(snapshot)
    redoStack.value = []
    return true
  }

  const reset = (canvas?: Canvas) => {
    undoStack.value = []
    redoStack.value = []
    if (canvas) save(canvas)
  }

  const restore = async (canvas: Canvas, snapshot: CanvasSnapshot) => {
    isRestoring.value = true
    await canvas.loadFromJSON(snapshot)
    canvas.requestRenderAll()
    isRestoring.value = false
  }

  const undo = async (canvas: Canvas) => {
    const previousIndex = getPreviousDistinctSnapshotIndex(undoStack.value)
    if (previousIndex < 0) return

    const current = undoStack.value[undoStack.value.length - 1]
    const previous = undoStack.value[previousIndex]
    undoStack.value = undoStack.value.slice(0, previousIndex + 1)
    redoStack.value.push(current)
    await restore(canvas, previous)
  }

  const redo = async (canvas: Canvas) => {
    const current = undoStack.value[undoStack.value.length - 1]
    let next: CanvasSnapshot | undefined
    while (redoStack.value.length) {
      const candidate = redoStack.value.pop()
      if (candidate && (!current || !snapshotsMatch(candidate, current))) {
        next = candidate
        break
      }
    }
    if (!next) return
    undoStack.value.push(next)
    await restore(canvas, next)
  }

  return {
    canUndo,
    canRedo,
    isRestoring,
    save,
    reset,
    undo,
    redo,
  }
}
